import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Badge, IconButton } from '@mui/material';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
// project imports

import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';
import useApi from 'hooks/useApi';
import { toLocalTime } from 'utils/dateUtils';
import { useOrganization } from 'contexts/OrganizationContext';
import { ThemeMode } from 'config';
import { getTypeTextColorForGrid } from 'store/constant';
import Cookies from 'universal-cookie';
import roleManagement from '../../../../utils/roleManagement';
import { resolveLiveSupportAccessSt } from '../../../../utils/accountContext/accountContextUtils';
// assets
import { IconBell, IconX } from '@tabler/icons-react';

const cookies = new Cookies();

/**
 * Mirrors the classic `getUserTotalAlarm` behavior: when the periodic poll
 * returns a fresh `supportAccessSt` value from the server, sync it to the
 * cookie and broadcast it so the banner / profile menu stay in sync without
 * a page refresh (e.g. when access is revoked from another tab or by support).
 */
const syncSupportAccessSt = (userInfo) => {
  if (!userInfo) return;

  // Mirrors classic `getUserTotalAlarm`: only update `supportAccessSt` for
  // users whose role/subRole has the TechSupport permission. Support sessions
  // (role or subRole === 'supportAgent') are excluded by this check, so the
  // banner never gets a state to render for them.
  let userRole;
  let userSubRole;
  try {
    userRole = roleManagement.getUserRole();
    userSubRole = roleManagement.getUserSubRole();
  } catch (e) {
    return;
  }
  if (!roleManagement.canGrantSupportAccessFromRoles(userRole, userSubRole)) {
    return;
  }

  const incomingStr = resolveLiveSupportAccessSt(userInfo, cookies.get('accountId'));
  if (incomingStr == null) return;

  const current = cookies.get('supportAccessSt');
  if (current === incomingStr) return;

  const secureCookie = process.env.secureCoockie || true;
  cookies.set('supportAccessSt', incomingStr, { secure: secureCookie, path: '/' });

  if (typeof window !== 'undefined' && window.dispatchEvent) {
    const eventName = incomingStr === '1' ? 'supportAccessGranted' : 'supportAccessRevoked';
    window.dispatchEvent(new CustomEvent(eventName));
  }
};

// ==============================|| NOTIFICATION ||============================== //

/** Bell badge: first non-zero allowlisted severity wins; priority follows NOTIFICATION_BELL_SEVERITY_ORDER. */
const NOTIFICATION_BELL_SEVERITY_ORDER = [
  { severity: 'critical', alarmType: '1' },
  { severity: 'warning', alarmType: '2' },
  { severity: 'info', alarmType: '3' }
];

/** Which severities participate in the bell (subset of keys in NOTIFICATION_BELL_SEVERITY_ORDER). */
const NOTIFICATION_BELL_SEVERITY_ALLOWLIST = ['critical', 'warning'];

export default function NotificationSection() {
  const intl = useIntl();
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationBadge, setNotificationBadge] = useState({
    count: 0,
    display: 0,
    severity: null, // 'critical' | 'warning' | 'info' | null
    alarmType: '' // '1' | '2' | '3' | ''
  });
  const pollingIntervalRef = useRef(null);
  const getNotificationCountRef = useRef(null);
  const getNotificationListRef = useRef(null);
  const pollingInFlightRef = useRef(false);
  const pollSeqRef = useRef(0);
  const mountedRef = useRef(false);
  const { orgId, scopeId, currentNetwork, updateOrganizationContext } = useOrganization();

  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => {
      const nextOpen = !prevOpen;
      if (!prevOpen && nextOpen) {
        // Fetch the latest notifications when opening the popover
        getNotificationList();
      }
      return nextOpen;
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  /**
   * Marks all notifications as read for the current user.
   * On success: updates in-memory list to 'read' and refreshes the badge count.
   */
  const handleMarkAsRead = async () => {
    if (notificationBadge?.count === 0) return;
    const previousBadge = notificationBadge;
    const previousNotifications = notifications;
    const optimisticNotifications = previousNotifications.map((n) => ({ ...n, status: 'read' }));

    setNotifications(optimisticNotifications);
    setNotificationBadge({ count: 0, display: 0, severity: null, alarmType: '' });

    try {
      const data = await useApi('MARK_ALL_AS_READ_NOTIFICATION', {}, {}, []);
      if (data && data.response && data.response.status) {
        getNotificationCount();
      } else {
        setNotificationBadge(previousBadge);
        setNotifications(previousNotifications);
      }
    } catch (e) {
      console.error('Failed to mark notifications as read', e);
      setNotificationBadge(previousBadge);
      setNotifications(previousNotifications);
    }
  };

  const navigateToAlarms = (notificationPreset) => {
    setOpen(false);
    // Already on Alarms — nothing to navigate to, and re-running the reset would just refetch.
    if (location.pathname === '/mspHome/notifications') {
      return;
    }
    // Must be absolute — a relative path resolves against the current route (e.g.
    // /organization/{orgName}/...) and lands on a URL that doesn't exist.
    // forceAllOrgsScope tells the Alarms page to treat scope as "all orgs" from its very
    // first render, regardless of what OrganizationContext still says — since that context's
    // own reset is deferred below, it hasn't caught up yet when the page mounts. Kept out of
    // notificationPreset so it can't be mistaken for a filter and applied to the server.
    navigate("/mspHome/notifications", { state: { notificationPreset, forceAllOrgsScope: true } });
    // Deferred: clearing org context synchronously here raced ScopeGuard, which saw the org
    // cleared while the URL hadn't changed yet and fired its own redirect to the dashboard,
    // beating this navigation. Waiting a tick lets the navigation commit first.
    setTimeout(() => {
      localStorage.setItem("scopeId", 0);
      // Label from intl — a hardcoded string leaks English into the hierarchy tree after a language switch.
      updateOrganizationContext(null, '0', null, { id: "All Organizations", label: intl.formatMessage({ id: 'allOrganizations' }), type: "All Organizations", icon: "All Organizations" });
    }, 0);
  };

  const handleViewAll = () => {
    navigateToAlarms();
  };

  const handleBellClick = () => {
    // If the bell badge is showing a severity (priority order), deep-link to alarms with preset filters:
    // - unread only
    // - selected severity only
    // orgId: [] is explicit — the bell always means "all organizations" — so Alarms doesn't
    // need to wait on the deferred org-scope reset above to know that.
    // time: '2' ("Last Week") — without it, reset:true defaults to "" (all time).
    if (notificationBadge?.alarmType) {
      navigateToAlarms({
        alarmType: notificationBadge.alarmType, // '1' | '2' | '3'
        status: '0', // unread
        reset: true, // ensure ONLY unread+severity is applied
        orgId: [],
        time: '2'
      });
      return;
    }
    // If we only know the total unread (no severity breakdown), still deep-link to Unread (all severities).
    if (notificationBadge?.count > 0) {
      navigateToAlarms({
        status: '0', // unread
        reset: true,
        orgId: [],
        time: '2'
      });
      return;
    }
    // No unread notifications: just open alarms page (no pre-filter)
    navigateToAlarms();
  };

  /**
   * Polls the server for the unread notification count and updates the badge.   
   */
  const getNotificationCount = async () => {    
    // - The API can authenticate via cookies when `withCredentials` is enabled on the client.
    if (pollingInFlightRef.current) return;

    const seq = ++pollSeqRef.current;
    pollingInFlightRef.current = true;

    const safeInt = (value) => {
      if (value === undefined || value === null || value === '') return 0;
      const normalized = String(value).trim();
      const numericValue = Number(normalized);
      return Number.isFinite(numericValue) ? Math.trunc(numericValue) : 0;
    };

    try {
      const data = await useApi('GET_NOTIFICATION_ALARM_COUNT', '', '', []);

      // Only let the latest request win, and never set state after unmount.
      if (!mountedRef.current || seq !== pollSeqRef.current) return;

      if (data && data.response && data.response.status) {
        // Backend payloads vary by environment/version. Some return counts at:        
        const rawUserInfo = data.userInfo || data.details?.userInfo || data.details || {};
        const nestedAlert =
          rawUserInfo?.alertDesc ||
          rawUserInfo?.alertdesc ||
          rawUserInfo?.alarmDesc ||
          rawUserInfo?.alarmdesc ||
          rawUserInfo?.alert ||
          {};
        const userInfo = { ...rawUserInfo, ...nestedAlert };

        // Mirror classic `getUserTotalAlarm`: keep supportAccessSt in sync from
        // the same poll so the Technical Support banner / menu reflect server
        // state (granted or revoked elsewhere) without requiring a refresh.
        syncSupportAccessSt(userInfo);

        // Support possible backend key variants (be liberal in what we accept).
        const rawCritical = userInfo.criticalUnreadAlarms ?? userInfo.criticalUnreadAlarm ?? userInfo.criticalUnread ?? userInfo.criticalUnreadCount;
        const rawWarning = userInfo.warningUnreadAlarms ?? userInfo.warnUnreadAlarms ?? userInfo.warningUnreadAlarm ?? userInfo.warningUnread ?? userInfo.warningUnreadCount;
        const rawInfo = userInfo.infoUnreadAlarms ?? userInfo.informationUnreadAlarms ?? userInfo.infoUnreadAlarm ?? userInfo.infoUnread ?? userInfo.infoUnreadCount;

        const isPresent = (value) => value !== undefined && value !== null && value !== '';
        const hasCompleteSeverityBreakdown = isPresent(rawCritical) && isPresent(rawWarning) && isPresent(rawInfo);

        const countsBySeverity = {
          critical: safeInt(rawCritical),
          warning: safeInt(rawWarning),
          info: safeInt(rawInfo)
        };
        const severityType = Object.fromEntries(NOTIFICATION_BELL_SEVERITY_ALLOWLIST.map((key) => [key, countsBySeverity[key] ?? 0]));
        const configuredSeverities = NOTIFICATION_BELL_SEVERITY_ALLOWLIST;

        let count = 0;
        let severity = null;
        let alarmType = '';

        const hasAnySeverityUnread = configuredSeverities.some((sev) => (severityType[sev] || 0) > 0);

        if (hasCompleteSeverityBreakdown || hasAnySeverityUnread) {
          const severityMatch = NOTIFICATION_BELL_SEVERITY_ORDER.find(
            (t) => configuredSeverities.includes(t.severity) && (severityType[t.severity] || 0) > 0
          );
          if (severityMatch) {
            count = severityType[severityMatch.severity];
            severity = severityMatch.severity;
            alarmType = severityMatch.alarmType;
          }
        }

        const display = count > 99 ? '99+' : count;
        setNotificationBadge({ count, display, severity, alarmType });
      } else if (data?.response?.responseCode === '8056') {
        // Clear polling if unauthorized
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      }
    } catch (error) {
      // ignore; next poll will retry
    } finally {
      // If a newer request started, keep inFlight true for that newer request.
      if (seq === pollSeqRef.current) {
        pollingInFlightRef.current = false;
      }
    }
  };

  const normalizeNotification = (notification) => {
    const titleParts = [];
    if (notification.networkName) titleParts.push(notification.networkName);
    if (notification.deviceName) titleParts.push(notification.deviceName);
    const displayTitle = titleParts.length ? titleParts.join(' - ') : '';
    return {
      id: notification._id,
      title: displayTitle || intl.formatMessage({ id: 'systemNotification' }),
      time: toLocalTime(notification.createdAt || notification.alarmCreatedAt, notification.nasCode),
      message: notification.description || notification.message || '',
      type:
        notification.alarmType === '1'
          ? 'Critical'
          : notification.alarmType === '2'
            ? 'Warning'
            : notification.alarmType === '3'
              ? 'Info'
              : 'Info',
      status: String(notification.status) === '1' ? 'read' : 'unread',
      organization: notification.orgName || '-',
      location: notification.networkName || '-'
    };
  };

  /**
   * Fetches correlated (child) alarms for a parent notification. Uses same notification list API with correlationId.
   * @param {string} correlationId - Correlation ID from the parent alarm
   * @param {number} page - Page number (0-based)
   * @returns {Promise<{ items: Array, hasMore: boolean }>}
   */
  const fetchCorrelatedAlarms = async (correlationId, page = 0) => {
    const params = {
      version: 'v1',
      alarmStatus: 'all',
      page,
      searchText: '',
      correlationId: `?correlationId=${encodeURIComponent(correlationId)}`,
      skipParams: '',
      stackUuidParam: ''
    };
    const body = {
      data: [
        { sortBy: 'alarmType', sortDir: 'asc' },
        { sortBy: 'createdAt', sortDir: 'desc' }
      ]
    };
    const data = await useApi('SHOW_NOTIFICATION_LIST', params, body, []);
    let flat = [];
    if (data && Array.isArray(data.info) && data.info.length && data.info[0]?._id) {
      flat = data.info;
    } else if (data && data.details && typeof data.details === 'object') {
      flat = Object.values(data.details).filter(Array.isArray).flat();
    } else if (Array.isArray(data?.details)) {
      flat = data.details;
    }
    const items = flat.map(normalizeNotification);
    const serverLimit =
      typeof data?.details?.muiPaginationLimit === 'number' && data.details.muiPaginationLimit > 0 ? data.details.muiPaginationLimit : 200;
    const totalCount = typeof data?.details?.count === 'number' ? data.details.count : null;
    const hasMore = totalCount === null ? items.length >= serverLimit : page * serverLimit + items.length < totalCount;
    return { items, hasMore };
  };

  /**
   * Fetches the latest notifications to populate the popover list.
   * Supports multiple API response shapes (legacy and current), flattens and normalizes fields, and limits to the most recent 15 notifications.
   */
  const getNotificationList = async () => {
    try {
      const data = await useApi('GET_NOTIFICATION_LIST', '', '', []);

      // Use the first 15 most recent notifications from the API response
      // API shapes supported:
      // - Legacy: data.info = [notifications]
      // - Current: data.details = { 'Month DD, YYYY': [notifications...] }
      let flat = [];
      if (data && Array.isArray(data.info) && data.info.length && data.info[0]?._id) {
        flat = data.info;
      } else if (data && data.details && typeof data.details === 'object') {
        flat = Object.values(data.details)
          .filter(Array.isArray)
          .flat();
      }

      if (Array.isArray(flat) && flat.length > 0) {
        // Sort by createdAt/alarmCreatedAt descending
        flat.sort((a, b) => {
          const notificationTimeA = parseInt(a.createdAt || a.alarmCreatedAt || 0, 10) || 0;
          const notificationTimeB = parseInt(b.createdAt || b.alarmCreatedAt || 0, 10) || 0;
          return notificationTimeB - notificationTimeA;
        });

        const firstFifteen = flat.slice(0, 15).map((notification) => ({
          ...normalizeNotification(notification),
          correlatable: Boolean(notification.correlatable),
          correlationId: notification.correlationId ?? null,
          correlationCount: notification.correlationCount ?? 0
        }));
        setNotifications(firstFifteen);
      }
    } catch (error) {
      console.error('Failed to fetch notification list', error);
    }
  };

  useEffect(() => {
    getNotificationCountRef.current = getNotificationCount;
    getNotificationListRef.current = getNotificationList;
  });

  useEffect(() => {
    const handleExternalMarkAllRead = (event) => {
      const action = event?.detail?.action || 'optimistic';

      if (action === 'optimistic') {
        setNotifications((prev) => prev.map((n) => ({ ...n, status: 'read' })));
        setNotificationBadge({ count: 0, display: 0, severity: null, alarmType: '' });
      } else if (action === 'revert') {
        if (typeof getNotificationCountRef.current === 'function') {
          getNotificationCountRef.current();
        }
        if (typeof getNotificationListRef.current === 'function') {
          getNotificationListRef.current();
        }
      }
    };

    window.addEventListener('imNotifications:markAllRead', handleExternalMarkAllRead);
    return () => {
      window.removeEventListener('imNotifications:markAllRead', handleExternalMarkAllRead);
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    getNotificationCount();

    // Start polling for notification count every 5 minutes
    pollingIntervalRef.current = setInterval(() => {
      getNotificationCount();
    }, 300000);

    // Cleanup interval on unmount
    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [orgId, scopeId, currentNetwork]);

  const badgeBgColor =
    notificationBadge?.severity === 'critical'
      ? getTypeTextColorForGrid('Critical')
      : notificationBadge?.severity === 'warning'
        ? getTypeTextColorForGrid('Warning') // match grid warning text color (and Figma)
        : notificationBadge?.severity === 'info'
          ? getTypeTextColorForGrid('Info')
          : undefined;

  return (
    <>
      <Box sx={{ ml: 1 }} id="notification-section-box">
        <IconButton
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleBellClick}
          sx={{
            color: 'white.paper',
            padding: 0.5,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
          id="notification-section-bell-icon-button"
        >
          <Badge
            badgeContent={notificationBadge?.count ? notificationBadge.display : 0}
            color="default"
            id="notification-section-badge"
            invisible={!notificationBadge?.count || notificationBadge.count === 0}
            sx={{
              '& .MuiBadge-badge': {
                ...(badgeBgColor ? { backgroundColor: badgeBgColor, color: '#fff' } : {}),
                fontSize: '0.75rem',
                minWidth: 20,
                height: 20,
                padding: '4px',
                top: 0,
                right: 3,
                textAlign: 'center'
              }
            }}
          >
            <IconBell stroke={1} size="26px" />
          </Badge>
        </IconButton>
      </Box>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        id="notification-section-popper"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [downMD ? 5 : 30, 10]
            }
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 20
            }
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['bottom-start', 'top-end', 'top-start']
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
              <Paper id="notification-section-paper">
                {open && (
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                    id="notification-section-main-card"
                    sx={{
                      width: { xs: '320px', sm: '530px' },
                      maxWidth: '100%',
                      border: '1px solid',
                      borderColor: theme.palette.mode === ThemeMode.DARK ? theme.palette.divider : 'transparent',
                      boxShadow: '0 9px 20px 0 rgba(0, 0, 0, 0.10)',
                      "&:hover": { boxShadow: '0 12px 24px 0 rgba(0, 0, 0, 0.12)' }
                    }}
                  >
                    <Grid container direction="column" spacing={0} id="notification-section-grid-container">
                      <Grid size={12} id="notification-section-grid-header">
                        <Grid
                          container
                          sx={{ alignItems: 'center', justifyContent: 'space-between', py: 2, px: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                          id="notification-section-grid-inner-container"
                        >
                          <Grid id="notification-section-grid-header-left">
                            <Typography variant="h2" id="notification-section-title" sx={{}}>
                              Alarms
                            </Typography>
                          </Grid>
                          <Grid id="notification-section-grid-header-right" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              component="span"
                              variant="body1"
                              id="notification-section-mark-as-read"
                              sx={{
                                cursor: 'pointer',
                                color: 'primary.main',
                                textDecoration: 'underline',
                              }}
                              onClick={handleMarkAsRead}
                            >
                              Mark All as Read
                            </Typography>
                            <IconButton
                              onClick={handleClose}
                            >
                              <IconX size="19px" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={12} id="notification-section-grid-content">
                        <Box
                          sx={{
                            flex: 1,
                            minHeight: 0,
                            maxHeight: 'calc(100vh - 240px)',
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': { width: 5 },
                            px: 2,
                            py: 2
                          }}
                          id="notification-section-scrollable-box"
                        >
                          <NotificationList
                            notifications={notifications}
                            onNotificationClick={handleViewAll}
                            onFetchCorrelatedAlarms={fetchCorrelatedAlarms}
                          />
                        </Box>
                      </Grid>
                      <CardActions sx={{ p: 2, justifyContent: 'flex-end', px: 2.5, borderTop: 'solid 1px', borderColor: 'divider' }} id="notification-section-card-actions">
                        <Button
                          variant='outlined'
                          disableElevation
                          id="notification-section-view-all-button"
                          onClick={handleViewAll}
                        >
                          View All
                        </Button>
                      </CardActions>
                    </Grid>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
